import { ActionBase } from '@/models/action_base';
import { KbnConst } from '@/utils/kbn';

import { useFileUpload } from './use_file_upload';
import { usePopupDialog } from './use_popup_dialog';

const MAX_FILE_PIXEL_SIZE = 24000000;

export class ActionReceiptPhotographing extends ActionBase {
  typeKbn = KbnConst.ACTION_TYPE_KBN_RECEIPT_PHOTOGRAPHING;

  selectImageBy: 'SELECTING_FILE' | 'PHOTOGRAPHING' = 'PHOTOGRAPHING';

  maxFileMegabytes: number = 5;

  use() {
    return async () => await execute(
      this.selectImageBy,
      this.maxFileMegabytes,
    );
  }

  constructor(init: Partial<ActionReceiptPhotographing> = {}) {
    super();
    Object.assign(this, init);
  }
}

function isJpgOrPng(file: File): boolean {
  if (file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png') {
    return true;
  } else {
    return false;
  }
}

// 画像の読み込みが完了すると解決される関数
// https://zenn.dev/kazh/articles/02101_promise_and_image
async function loadImage(file: File): Promise<HTMLImageElement> {
  const img = new Image();

  return new Promise(
    (resolve) => {
      img.onload = () => {
        resolve(img);
      };
      img.src = URL.createObjectURL(file);
    },
  );
}

async function calculateImageSize(file: File) {
  const img = await loadImage(file);

  const width = img.naturalWidth;
  const height = img.naturalHeight;

  URL.revokeObjectURL(img.src);

  return {
    width,
    height,
  };
}

async function execute(
  selectImageBy: 'SELECTING_FILE' | 'PHOTOGRAPHING',
  maxFileMegabytes: number,
) {
  const fileUpload = useFileUpload();
  const popupDialog = usePopupDialog();

  if (selectImageBy === 'SELECTING_FILE') {
    if (await fileUpload.uploadPicture()) {
      return true;
    }
  } else if (selectImageBy === 'PHOTOGRAPHING') {
    if (await fileUpload.takePicture()) {
      return true;
    }
  }

  const file = fileUpload.fileCache;
  if (!file.value) {
    return true;
  }

  const maxFileSize = maxFileMegabytes * 1024 * 1024;
  if (file.value.size > maxFileSize) {
    popupDialog.alert(`${maxFileMegabytes}MB以下のファイルをアップしてください`);
    fileUpload.fileCache.value = undefined;
    return true;
  }
  if (!isJpgOrPng(file.value)) {
    await popupDialog.alert('JPEG画像またはPNG画像をアップロードしてください');
    fileUpload.fileCache.value = undefined;
    return true;
  }
  const { width, height } = await calculateImageSize(file.value);
  if (width * height > MAX_FILE_PIXEL_SIZE) {
    popupDialog.alert(`${MAX_FILE_PIXEL_SIZE / 10000}万画素以下の画像をアップしてください`);
    fileUpload.fileCache.value = undefined;
    return true;
  }
  return false;
}

export function generateActionReceiptPhotographing() {
  return new ActionReceiptPhotographing();
}
